/**
 * Shared MapLibre helpers for the TomTom traffic stack + emergency services
 * + crowd accidents + turn-by-turn routing.
 *
 * Used by both the citizen Carte (/carte) and the admin Carte tactique tab so
 * dispatchers and citizens see the exact same traffic intelligence — and
 * adding/upgrading a layer means changing one file, not two.
 *
 * All endpoints are routed through `${API}/map/...` so the TomTom API key
 * stays server-side.
 */

import maplibregl from "maplibre-gl";
import { API } from "./api";
import { SERVICE_CATEGORIES, buildServicesGeoJSON } from "../data/maliEmergencyServices";

export const SOURCE_IDS = {
  FLOW:   "tomtom-flow",
  INC:    "tomtom-incidents",
  CROWD:  "crowd-accidents",
  SVC:    "emergency-services",
  ROUTE:  "tomtom-route",
};

export const LAYER_IDS = {
  FLOW_TILES:     "tomtom-flow-layer",
  INC_HALO:       "tomtom-incidents-halo",
  INC_DOT:        "tomtom-incidents-dot",
  CROWD_HALO:     "crowd-accidents-halo",
  CROWD_DOT:      "crowd-accidents-dot",
  SVC_DOT:        "svc-bg",
  SVC_LABEL:      "svc-label",
  ROUTE_CASING:   "route-casing",
  ROUTE_LINE:     "route-line",
};

// TomTom incident iconCategory → label + accent. Same table as MapPage uses.
const TOMTOM_ICON = {
  1:  { label: "Accident",          color: "#DC2626" },
  3:  { label: "Danger",            color: "#F97316" },
  6:  { label: "Embouteillage",     color: "#EAB308" },
  7:  { label: "Voie fermée",       color: "#F59E0B" },
  8:  { label: "Route fermée",      color: "#7C2D12" },
  9:  { label: "Travaux",           color: "#F59E0B" },
  11: { label: "Inondation",        color: "#0EA5E9" },
  14: { label: "Véhicule en panne", color: "#9333EA" },
};
export const tomtomMeta = (cat) =>
  TOMTOM_ICON[cat] || { label: "Incident", color: "#64748B" };

/** Insert TomTom Traffic Flow raster (hidden by default). `beforeId` optional. */
export const addTomTomFlowLayer = (map, beforeId) => {
  if (!map.getSource(SOURCE_IDS.FLOW)) {
    map.addSource(SOURCE_IDS.FLOW, {
      type: "raster",
      tiles: [`${API}/map/traffic/flow/{z}/{x}/{y}.png?tileSize=256&thickness=8`],
      tileSize: 256,
      minzoom: 0,
      maxzoom: 22,
      attribution: "Trafic © TomTom",
    });
  }
  if (!map.getLayer(LAYER_IDS.FLOW_TILES)) {
    map.addLayer({
      id: LAYER_IDS.FLOW_TILES,
      type: "raster",
      source: SOURCE_IDS.FLOW,
      paint: { "raster-opacity": 0.78 },
      layout: { visibility: "none" },
    }, beforeId);
  }
};

/** TomTom Incident pins (hidden by default). */
export const addTomTomIncidentLayers = (map) => {
  if (!map.getSource(SOURCE_IDS.INC)) {
    map.addSource(SOURCE_IDS.INC, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  }
  if (!map.getLayer(LAYER_IDS.INC_HALO)) {
    map.addLayer({
      id: LAYER_IDS.INC_HALO,
      type: "circle",
      source: SOURCE_IDS.INC,
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["coalesce", ["get", "magnitudeOfDelay"], 1], 1, 10, 4, 18],
        "circle-color": ["coalesce", ["get", "color"], "#64748B"],
        "circle-opacity": 0.18,
      },
      layout: { visibility: "none" },
    });
  }
  if (!map.getLayer(LAYER_IDS.INC_DOT)) {
    map.addLayer({
      id: LAYER_IDS.INC_DOT,
      type: "circle",
      source: SOURCE_IDS.INC,
      paint: {
        "circle-radius": 7,
        "circle-color": ["coalesce", ["get", "color"], "#64748B"],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2.5,
      },
      layout: { visibility: "none" },
    });
  }
};

/** Crowd-sourced accident pulses (visible by default) — Carte v3 layered glow. */
export const addCrowdAccidentLayers = (map) => {
  if (!map.getSource(SOURCE_IDS.CROWD)) {
    map.addSource(SOURCE_IDS.CROWD, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  }
  if (!map.getLayer(LAYER_IDS.CROWD_HALO)) {
    map.addLayer({
      id: LAYER_IDS.CROWD_HALO,
      type: "circle",
      source: SOURCE_IDS.CROWD,
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 14, 16, 28],
        "circle-color": "#EAB308",
        "circle-opacity": 0.22,
        "circle-blur": 0.7,
      },
    });
  }
  if (!map.getLayer(LAYER_IDS.CROWD_DOT)) {
    map.addLayer({
      id: LAYER_IDS.CROWD_DOT,
      type: "circle",
      source: SOURCE_IDS.CROWD,
      paint: {
        "circle-radius": 8,
        "circle-color": "#EAB308",
        "circle-stroke-color": "#0F1428",
        "circle-stroke-width": 2.5,
      },
    });
  }
};

/** Emergency-service pins (hospitals/police/fire/FAMA/embassies) — Carte v3
 *  premium style: outer glow halo + glassy white-outlined dot. */
export const addEmergencyServiceLayers = (map, activeKeys) => {
  if (!map.getSource(SOURCE_IDS.SVC)) {
    map.addSource(SOURCE_IDS.SVC, { type: "geojson", data: buildServicesGeoJSON(activeKeys) });
  }
  // Soft glow halo (sits behind the dot for depth)
  if (!map.getLayer("svc-glow")) {
    map.addLayer({
      id: "svc-glow",
      type: "circle",
      source: SOURCE_IDS.SVC,
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 8, 14, 18, 18, 26],
        "circle-color": ["get", "color"],
        "circle-opacity": 0.22,
        "circle-blur": 0.8,
      },
    });
  }
  if (!map.getLayer(LAYER_IDS.SVC_DOT)) {
    map.addLayer({
      id: LAYER_IDS.SVC_DOT,
      type: "circle",
      source: SOURCE_IDS.SVC,
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 7, 14, 11, 18, 14],
        "circle-color": ["get", "color"],
        "circle-opacity": 1,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2.5,
        "circle-pitch-alignment": "map",
      },
    });
  }
  if (!map.getLayer(LAYER_IDS.SVC_LABEL)) {
    map.addLayer({
      id: LAYER_IDS.SVC_LABEL,
      type: "symbol",
      source: SOURCE_IDS.SVC,
      minzoom: 13,
      layout: {
        "text-field": ["get", "name"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 13, 9, 18, 12],
        "text-offset": [0, 1.5],
        "text-anchor": "top",
        "text-optional": true,
        "text-letter-spacing": 0.02,
        "text-font": ["Noto Sans Regular"],
      },
      paint: {
        "text-color": "#0F172A",
        "text-halo-color": "rgba(255,255,255,0.95)",
        "text-halo-width": 1.6,
      },
    });
  }
};

/** TomTom Routing polyline (Carte v3 — glow underneath emerald path). */
export const addRouteLayer = (map) => {
  if (!map.getSource(SOURCE_IDS.ROUTE)) {
    map.addSource(SOURCE_IDS.ROUTE, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  }
  if (!map.getLayer("route-glow")) {
    map.addLayer({
      id: "route-glow",
      type: "line",
      source: SOURCE_IDS.ROUTE,
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#10B981", "line-width": 18, "line-opacity": 0.18, "line-blur": 6 },
    });
  }
  if (!map.getLayer(LAYER_IDS.ROUTE_CASING)) {
    map.addLayer({
      id: LAYER_IDS.ROUTE_CASING,
      type: "line",
      source: SOURCE_IDS.ROUTE,
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#0F1428", "line-width": 9, "line-opacity": 0.9 },
    });
  }
  if (!map.getLayer(LAYER_IDS.ROUTE_LINE)) {
    map.addLayer({
      id: LAYER_IDS.ROUTE_LINE,
      type: "line",
      source: SOURCE_IDS.ROUTE,
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#10B981", "line-width": 5 },
    });
  }
};

export const setLayerVisible = (map, id, on) => {
  if (map.getLayer(id)) {
    try { map.setLayoutProperty(id, "visibility", on ? "visible" : "none"); } catch (_e) { /* noop */ }
  }
};

/** Fetch + paint TomTom incidents for the current viewport. */
export const fetchAndApplyIncidents = async (map) => {
  const b = map.getBounds();
  const sw = b.getSouthWest(); const ne = b.getNorthEast();
  const bbox = `${sw.lng.toFixed(4)},${sw.lat.toFixed(4)},${ne.lng.toFixed(4)},${ne.lat.toFixed(4)}`;
  try {
    const r = await fetch(`${API}/map/traffic/incidents?bbox=${encodeURIComponent(bbox)}`);
    if (!r.ok) return;
    const fc = await r.json();
    const enriched = (fc.features || []).map((f) => {
      const cat = f.properties?.iconCategory;
      const meta = tomtomMeta(cat);
      return {
        ...f,
        geometry: f.geometry?.type === "LineString"
          ? { type: "Point", coordinates: f.geometry.coordinates[Math.floor(f.geometry.coordinates.length / 2)] }
          : f.geometry,
        properties: { ...f.properties, color: meta.color, label: meta.label },
      };
    }).filter((f) => f.geometry && f.geometry.coordinates);
    const src = map.getSource(SOURCE_IDS.INC);
    if (src) src.setData({ type: "FeatureCollection", features: enriched });
  } catch (_e) { /* noop */ }
};

export const fetchAndApplyCrowdAccidents = async (map, sinceHours = 24) => {
  try {
    const r = await fetch(`${API}/map/crowd-accidents?since_hours=${sinceHours}`);
    if (!r.ok) return;
    const fc = await r.json();
    const src = map.getSource(SOURCE_IDS.CROWD);
    if (src) src.setData(fc);
  } catch (_e) { /* noop */ }
};

/** Request a TomTom turn-by-turn route. Returns {summary, geometry, steps}. */
export const requestRoute = async ({ originLat, originLng, destLat, destLng, avoid }) => {
  const params = new URLSearchParams({
    origin: `${originLat},${originLng}`,
    dest: `${destLat},${destLng}`,
  });
  if (avoid) params.set("avoid", avoid);
  const r = await fetch(`${API}/map/route?${params.toString()}`);
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`Route HTTP ${r.status}: ${detail.slice(0, 160)}`);
  }
  return r.json();
};

/** Render a route response on the map + fit bounds to it. */
export const renderRouteOnMap = (map, routeResp) => {
  if (!routeResp || !routeResp.geometry) return;
  const src = map.getSource(SOURCE_IDS.ROUTE);
  const fc = {
    type: "FeatureCollection",
    features: [{ type: "Feature", properties: {}, geometry: routeResp.geometry }],
  };
  if (src) src.setData(fc);
  // Fit bounds to the route
  const coords = routeResp.geometry.coordinates;
  if (coords && coords.length >= 2) {
    const bounds = coords.reduce(
      (b, c) => b.extend(c),
      new maplibregl.LngLatBounds(coords[0], coords[0]),
    );
    map.fitBounds(bounds, { padding: 80, duration: 800, maxZoom: 16 });
  }
};

export const clearRoute = (map) => {
  const src = map.getSource(SOURCE_IDS.ROUTE);
  if (src) src.setData({ type: "FeatureCollection", features: [] });
};

export { SERVICE_CATEGORIES };
